import { ALARM_TYPES, buildSiteTree, localDayRange, type Alarm, type SiteNode } from "@fts/core";
import { useEffect, useMemo, useRef, useState } from "react";
import { DateField } from "../components/DateField";
import { Icon } from "../components/Icon";
import { EmptyState, Loading, PageHeader, Panel } from "../components/ui";
import { useI18n } from "../i18n";
import { alarmTitle, alarmTone, alarmTypeLabel, canAcknowledge, canAcknowledgeAlarm, formatAlarmTime, parseStatusFilter, validDay } from "../lib/alarms";
import { descendantIds } from "../lib/fleet";
import { navigate, type Route } from "../router/hash";
import { useAlarmCount } from "../state/alarms";
import { useSession, type TenantData } from "../state/session";
import { QuickAckDialog, rememberAlarmList, useOptimisticAck } from "./AlarmAck";
import { AlarmBadges, AlarmStatusBadge } from "./AlarmParts";

/** Newest alarms fetched per query; older ones are reached by narrowing the date range. */
export const ALARM_LIST_LIMIT = 500;

function siteOptions(tree: SiteNode[], depth = 0, out: Array<{ id: string; label: string }> = []) {
  for (const n of tree) {
    out.push({ id: n.id, label: `${"   ".repeat(depth)}${n.name}` });
    siteOptions(n.children, depth + 1, out);
  }
  return out;
}

export function operatorName(data: TenantData, operatorId: string | null): string | null {
  if (!operatorId) return null;
  const o = data.operators.operators.find((x) => x.id === operatorId);
  // Anonymised (departed) operators keep their id; their name shows as "—" (ADR 0011, KVKK).
  return o && !o.anonymized ? o.fullName : null;
}

export function AlarmsList({ data, route }: { data: TenantData; route: Route }) {
  const { t, locale } = useI18n();
  const { provider, tenant, role, user } = useSession();
  const { version } = useAlarmCount();
  const acknowledge = useOptimisticAck();
  const [acking, setAcking] = useState<Alarm | null>(null);
  const [focusAfter, setFocusAfter] = useState<{ id: string | null; n: number } | null>(null);
  const status = parseStatusFilter(route.query.get("status"));
  const typeParam = route.query.get("type");
  const type = typeParam && (ALARM_TYPES as readonly string[]).includes(typeParam) ? (typeParam as Alarm["type"]) : null;
  const site = route.query.get("site");
  const siteKnown = site && data.sites.some((s) => s.id === site) ? site : null;
  const from = validDay(route.query.get("from"));
  const to = validDay(route.query.get("to"));
  const dateError = !!(from && to && to < from);
  const [state, setState] = useState<{ alarms: Alarm[]; loaded: boolean; error: boolean }>({ alarms: [], loaded: false, error: false });
  const options = useMemo(() => siteOptions(buildSiteTree(data.sites, [])), [data.sites]);
  const tenantId = tenant?.id;
  /**
   * Acknowledgements in flight: the optimistic row and the newest copy a reload (badge polling, filter change)
   * returned meanwhile. Reloads show the optimistic row; a failure rolls back to the newest server copy.
   */
  const pending = useRef(new Map<string, { optimistic: Alarm; server: Alarm | null }>());

  useEffect(() => {
    if (!tenantId || dateError) return;
    let cancelled = false;
    const range = localDayRange(from, to);
    provider
      .listAlarms(tenantId, {
        status: status === "all" ? null : status,
        type,
        siteIds: siteKnown ? [...descendantIds(data.sites, siteKnown)] : null,
        from: range.from,
        to: range.to,
        limit: ALARM_LIST_LIMIT,
      })
      .then((alarms) => {
        if (cancelled) return;
        const merged = alarms.map((x) => {
          const p = pending.current.get(x.id);
          if (!p) return x;
          p.server = x;
          return p.optimistic;
        });
        setState({ alarms: merged, loaded: true, error: false });
      })
      .catch(() => !cancelled && setState((s) => ({ ...s, loaded: true, error: true })));
    return () => {
      cancelled = true;
    };
  }, [provider, tenantId, status, type, siteKnown, from, to, dateError, data.sites, version]);

  // "Back to alarms" from a detail page returns to this list with its filters.
  const queryString = route.query.toString();
  useEffect(() => rememberAlarmList(`#/alarms${queryString ? `?${queryString}` : ""}`), [queryString]);

  // After a quick acknowledgement the row's button is gone: move focus to the next open alarm (or the list).
  useEffect(() => {
    if (!focusAfter) return;
    const next = focusAfter.id ? [...document.querySelectorAll<HTMLElement>("[data-ack-id]")].find((el) => el.dataset.ackId === focusAfter.id) : null;
    (next ?? document.getElementById("alarm-list"))?.focus();
  }, [focusAfter]);

  const mayAck = (a: Alarm) => a.status === "open" && !!tenantId && canAcknowledgeAlarm(user, tenantId, role, a);
  const showAckColumn = canAcknowledge(role);
  /** `apply` for useOptimisticAck: first the optimistic row, then (only on failure) the original again. */
  const applyAck = (original: Alarm) => (next: Alarm) => {
    if (next !== original) {
      pending.current.set(original.id, { optimistic: next, server: null });
      setState((s) => ({ ...s, alarms: s.alarms.map((x) => (x.id === original.id ? next : x)) }));
      return;
    }
    const p = pending.current.get(original.id);
    pending.current.delete(original.id);
    const target = p?.server ?? original;
    // Roll back only a row that still shows our optimistic state; anything newer stays.
    setState((s) => ({ ...s, alarms: s.alarms.map((x) => (x.id === original.id && p && x === p.optimistic ? target : x)) }));
  };
  const label = (a: Alarm) => {
    const v = a.vehicleId ? data.vehicles.find((x) => x.id === a.vehicleId)?.fleetNo : undefined;
    return v ? `${alarmTitle(t, a)} · ${v}` : alarmTitle(t, a);
  };
  const submitAck = (a: Alarm, note: string | null) => {
    const open = state.alarms.filter(mayAck);
    const i = open.findIndex((x) => x.id === a.id);
    const next = open[i + 1] ?? open[i - 1] ?? null;
    setAcking(null);
    setFocusAfter((f) => ({ id: next?.id ?? null, n: (f?.n ?? 0) + 1 }));
    void acknowledge(a, note, applyAck(a), { label: label(a) }).then((r) => {
      if (r.ok) pending.current.delete(a.id);
    });
  };

  const update = (patch: Record<string, string | null>) => {
    const next = { status: status === "open" ? null : status, type, site: siteKnown, from, to, ...patch };
    navigate("/alarms", {
      status: next.status || undefined,
      type: next.type || undefined,
      site: next.site || undefined,
      from: next.from || undefined,
      to: next.to || undefined,
    });
  };
  const hasFilter = status !== "open" || !!(type || siteKnown || from || to);
  const siteName = (id: string | null) => (id ? data.sites.find((s) => s.id === id)?.name : undefined) ?? t("common.empty");
  const vehicle = (id: string | null) => (id ? data.vehicles.find((v) => v.id === id) : undefined);

  return (
    <>
      <PageHeader title={t("alarms.title")} subtitle={t("alarms.subtitle")} />
      <div className="filters" role="group" aria-label={t("alarms.filtersLabel")}>
        <div className="field field-inline">
          <label htmlFor="f-astatus">{t("alarms.filter.status")}</label>
          <select id="f-astatus" value={status} onChange={(e) => update({ status: e.target.value === "open" ? null : e.target.value })}>
            <option value="open">{t("alarms.filter.open")}</option>
            <option value="acknowledged">{t("alarms.filter.acknowledged")}</option>
            <option value="all">{t("alarms.filter.all")}</option>
          </select>
        </div>
        <div className="field field-inline">
          <label htmlFor="f-atype">{t("alarms.filter.type")}</label>
          <select id="f-atype" value={type ?? ""} onChange={(e) => update({ type: e.target.value || null })}>
            <option value="">{t("alarms.filter.allTypes")}</option>
            {ALARM_TYPES.map((x) => (
              <option key={x} value={x}>
                {alarmTypeLabel(t, x)}
              </option>
            ))}
          </select>
        </div>
        <div className="field field-inline">
          <label htmlFor="f-asite">{t("alarms.filter.site")}</label>
          <select id="f-asite" value={siteKnown ?? ""} onChange={(e) => update({ site: e.target.value || null })}>
            <option value="">{t("alarms.filter.allSites")}</option>
            {options.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div className="field field-inline">
          <label htmlFor="f-afrom">{t("alarms.filter.from")}</label>
          <DateField id="f-afrom" value={from ?? ""} max={to ?? undefined} onChange={(v) => update({ from: v || null })} />
        </div>
        <div className="field field-inline">
          <label htmlFor="f-ato">{t("alarms.filter.to")}</label>
          <DateField id="f-ato" value={to ?? ""} min={from ?? undefined} invalid={dateError} describedBy={dateError ? "f-ato-error" : undefined} onChange={(v) => update({ to: v || null })} />
        </div>
        {hasFilter && (
          <button type="button" className="link-button" onClick={() => navigate("/alarms")}>
            {t("alarms.filter.clear")}
          </button>
        )}
      </div>
      {dateError && (
        <p id="f-ato-error" className="form-error" role="alert">
          {t("alarms.filter.dateError")}
        </p>
      )}
      {state.error && (
        <p className="form-error" role="alert">
          {t("alarms.loadError")}
        </p>
      )}
      <Panel flush meta={state.loaded ? <span>{t("alarms.shown", { count: state.alarms.length })}</span> : undefined}>
        {!state.loaded ? (
          <Loading shape="table" />
        ) : state.alarms.length === 0 ? (
          <EmptyState
            compact
            icon={status === "open" && !hasFilter ? "check" : "search"}
            title={status === "open" && !hasFilter ? t("alarms.emptyOpen") : t("alarms.empty")}
            action={
              hasFilter ? (
                <button type="button" className="button button-secondary button-sm" onClick={() => navigate("/alarms")}>
                  {t("alarms.filter.clear")}
                </button>
              ) : undefined
            }
          />
        ) : (
          <div className="table-wrap" id="alarm-list" tabIndex={-1}>
            <table className="table alarm-table table-cards">
              <thead>
                <tr>
                  <th scope="col">{t("alarms.col.time")}</th>
                  <th scope="col">{t("alarms.col.type")}</th>
                  <th scope="col">{t("alarms.col.vehicle")}</th>
                  <th scope="col" className="col-hide-sm">
                    {t("alarms.col.site")}
                  </th>
                  <th scope="col" className="col-hide-md">
                    {t("alarms.col.operator")}
                  </th>
                  <th scope="col">{t("alarms.col.status")}</th>
                  {showAckColumn && (
                    <th scope="col" className="col-actions">
                      <span className="sr-only">{t("common.actions")}</span>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {state.alarms.map((a) => {
                  const v = vehicle(a.vehicleId);
                  const time = formatAlarmTime(a.occurredAt, locale) ?? t("common.empty");
                  return (
                    <tr key={a.id}>
                      <td className="nowrap cell-title" data-label={t("alarms.col.time")}>
                        <a
                          href={`#/alarms/${encodeURIComponent(a.id)}`}
                          className="row-link"
                          aria-label={t("alarms.rowLink", { type: alarmTitle(t, a), time })}
                        >
                          {time}
                        </a>
                      </td>
                      <td data-label={t("alarms.col.type")}>
                        <span className="alarm-type">
                          <span className={`status-dot tone-alarm-${alarmTone(a)}`} aria-hidden="true" />
                          {alarmTitle(t, a)}
                        </span>
                        <AlarmBadges alarm={a} />
                      </td>
                      <td data-label={t("alarms.col.vehicle")}>
                        {v ? (
                          <a href={`#/vehicles/${encodeURIComponent(v.id)}`} className="subtle-link mono">
                            {v.fleetNo}
                          </a>
                        ) : (
                          t("common.empty")
                        )}
                      </td>
                      <td className="col-hide-sm" data-label={t("alarms.col.site")}>{siteName(a.siteId)}</td>
                      <td className="col-hide-md" data-label={t("alarms.col.operator")}>{operatorName(data, a.operatorId) ?? t("common.empty")}</td>
                      <td data-label={t("alarms.col.status")}>
                        <AlarmStatusBadge status={a.status} />
                      </td>
                      {showAckColumn && (
                        <td className="col-actions">
                          {mayAck(a) && (
                            <button
                              type="button"
                              className="button button-secondary button-sm"
                              data-ack-id={a.id}
                              onClick={() => setAcking(a)}
                              aria-label={t("alarmQuick.rowButton", { type: alarmTitle(t, a), time })}
                            >
                              <Icon name="check" size={15} />
                              <span className="button-label">{t("alarmQuick.button")}</span>
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      {state.loaded && state.alarms.length >= ALARM_LIST_LIMIT && <p className="panel-footnote">{t("alarms.limitNote", { limit: ALARM_LIST_LIMIT })}</p>}
      {acking && (
        <QuickAckDialog
          alarm={acking}
          title={alarmTitle(t, acking)}
          vehicle={vehicle(acking.vehicleId)?.fleetNo ?? t("common.empty")}
          time={formatAlarmTime(acking.occurredAt, locale) ?? t("common.empty")}
          onClose={() => setAcking(null)}
          onSubmit={(note) => submitAck(acking, note)}
        />
      )}
    </>
  );
}
